import { PEERS_LIMIT, REGIONS } from '@/pages/CompanyReport/constants';
import { useQuery } from '@tanstack/react-query';
import { useSelector, shallowEqual } from 'react-redux';
import {
  getHistogramQueryKey,
  getPeersQueryKey,
  queryHistogramFn,
  queryPeersFn,
} from '../query';
import selector from '../selector';

export default function useValuationDescriptions() {
  const selected = useSelector<
    Parameters<typeof selector>[0],
    ReturnType<typeof selector>
  >(selector, shallowEqual);

  const { data, status } = useQuery({
    queryKey: getHistogramQueryKey(selected.canonicalUrl),
    queryFn: queryHistogramFn,
    staleTime: Infinity,
    enabled: Boolean(selected.canonicalUrl),
  });

  const { data: companyData } = useQuery({
    queryKey: getPeersQueryKey(selected.canonicalUrl, PEERS_LIMIT),
    queryFn: queryPeersFn,
    enabled: Boolean(selected.canonicalUrl),
  });
  const getIndustrySource = (
    industryFallbackLevel: string | undefined,
    countryName: string,
    regionName: string
  ) => {
    if (industryFallbackLevel === 'country') return countryName;
    if (industryFallbackLevel === 'region') return regionName;
    return 'Global';
  };
  const { name: multipleName } = selected.multiples.preferredMultiple;
  const {
    tickerSymbol,
    countryISO: countryName,
    industrySecondaryName: industryName,
  } = selected;
  const industryFallbackLevel = data?.Company.industryHistogram.level;
  const regionId = companyData?.Company.country.region.id;
  const regionName = regionId ? REGIONS[regionId]?.adjective : '';
  const vsIndustrySource = getIndustrySource(
    industryFallbackLevel,
    countryName,
    regionName
  );
  const valuationDescription = `Is ${tickerSymbol} undervalued compared to its fair value, analyst forecasts and its price relative to the market?`;
  const vsPeersDescription = `How does ${tickerSymbol}'s ${multipleName} Ratio compare to its peers?`;
  const getVsIndustryDescription = (multiple: string) =>
    status === 'success'
      ? `How does ${tickerSymbol}'s ${multiple} Ratio compare vs other companies in the ${vsIndustrySource} ${industryName} Industry?`
      : `How does ${tickerSymbol}'s ${multiple} Ratio compare vs other companies in the ${
          typeof industryName === 'string' ? industryName : ''
        } Industry?`;
  const vsFairValueDescription = `What is the Fair Price of ${tickerSymbol} when looking at its future cash flows? For this estimate we use a Discounted Cash Flow model.`;
  const fairRatioPreDescription = `What is ${tickerSymbol}'s ${multipleName} Ratio compared to its `;
  const fairRatioDescription = `Fair ${multipleName} Ratio`;
  const fairRatioPostDescription = `? This is the expected ${multipleName} Ratio taking into account the company's forecast earnings growth, profit margins and other risk factors.`;
  const analystPriceTargetsDescription =
    'What is the analyst 12-month forecast and do we have any statistical confidence in the consensus price target?';
  return {
    valuationDescription,
    vsPeersDescription,
    getVsIndustryDescription,
    vsFairValueDescription,
    fairRatioPreDescription,
    fairRatioDescription,
    fairRatioPostDescription,
    analystPriceTargetsDescription,
  };
}
